/* Query Keys */
export enum QUERY_KEYS {
  EBILL_ELIGIBILITY_KEY = 'GET_EBILL_ELIGIBILITY',
  HSA_ELIGIBILITY_KEY = 'GET_HSA_ELIGIBILITY',
  BACKGROUND_INFORMATION = 'background_information',
  DEMOGRAPHICS_PROFILE = 'demographics_profile',
  GET_CLAIMS_DISCLAIMER_STATUS_BANNER = 'banner$claims_disclaimer_status_banner',
}

export const DEFAULT_PARTNER_ID = 'highmark';
export const AUTH0_CLIENT_CONNECTION = 'highmark';

/* Benefit Booklet Download */
export const BENEFIT_BOOKLET_DOWNLOAD_PREFIX = 'download_benefit_booklet:';
// This URL is a marker that will be intercepted by LinkHandler.handleLink
// It needs to look like a valid URL to pass through the League SDK's URL validation
export const BENEFIT_BOOKLET_DOWNLOAD_URL_MARKER =
  'https://benefit-booklet-download.highmark.internal/download';
export const BENEFIT_BOOKLET_DOCUMENT_KEY_PARAM = 'documentKey';
export const SUPPORT_ROOT_NODE_ID = 'supportPage$root';
export const HOME_ROOT_NODE_ID = 'homePage$root';
export const BENEFITS_LANDING_ROOT_NODE_ID = 'benefits$landingPage$root';
export const BENEFITS_DETAIL_ROOT_NODE_ID = 'benefits$detailPage$root';
export const CASE_MANAGEMENT_ROOT_NODE_ID = 'flows$caseManagementWeb$root';

/* Nav Bar */
export const APP_NAV_DEFAULT_HEIGHT = 8 * 7; // 56px – 7 lines on the Genesis 8px grid
export const APP_NAV_MOBILE_HEIGHT = APP_NAV_DEFAULT_HEIGHT * 2; // Mobile Nav has 2 rows, so twice the default height

export const SENTRY_CONSTANTS = {
  OWNERSHIP_TEAM: 'highmark-solutions',
};

export const AUTH0_ERROR_TYPES = {
  LOGIN_REQUIRED: 'login_required',
};

// Feature Flag Names
export enum FEATURE_FLAGS {
  PLACEHOLDER_FLAG = 'do_not_use', // Do not remove this non-functional flag: keep feature flag logic present even when we have 0 active flags
  /* Add active flags below this line */
  CLAIMS_SHOULD_SHOW_CLAIM_DETAILS = 'rel_claims_shouldShowClaimDetails',
  CLAIMS_SHOULD_SHOW_DOWNLOAD_CLAIMS = 'rel_claims_shouldShowDownloadClaims',
  CLAIMS_SHOULD_SHOW_FILTER_CHIPS = 'rel_claims_shouldShowFilterChips',
  CLAIMS_ENABLE_DYNAMIC_FILTERS = 'release_claims_configurable_filters',
  USER_DROPDOWN_COBROWSE = 'rel_hmrk_enableGlia',
  CMS_CODE = 'bus_hmrk_cmsCode',
  FORMS_LIBRARY = 'rel_hmrk_showFormsLibrary',
  COB_SDK = 'rel_hmrk_cobSDK',
  WALLET_ENABLE_NO_NETWORK_LIVE_BALANCES = 'release_wallet_no_network',
  ENABLE_GENESIS_CORE_INSPECTOR = 'rel_hmrk_enableGenesisInspector',
  LIVE_CHAT_NAVIGATE_AWAY = 'experiment_live_chat_navigate_away',
  PWP = 'rel_hmrk_pwp',
  CLAIMS_SHOULD_SHOW_CLAIM_INSURANCE_GLOSSARY = 'rel_claims_showClaimInsuranceGlossary',
  ENABLE_SUPPORT_PAGE_2_0 = 'bus_support_enableMasonry2_0',
  ENABLE_HOME_PAGE_2_0 = 'bus_home_enableMasonry2_0',
  ENABLE_BENEFITS_PAGE_2_0 = 'enable_masonry_2_benefits_experience',
  ENABLE_NAVIGATION_ENHANCEMENTS = 'release_highmark_web_nav',
  ENABLE_HOME_SCREEN_ENHANCEMENTS = 'highmark_homescreen_enhancements_ff',
  DOCUMENT_CENTER_UM_LETTERS = 'release_documents_enable_um_letters',
  DOCUMENT_CENTER_WELCOME_GUIDES = 'release_documents_enable_welcome_guides',
  DOCUMENT_CENTER_BENEFIT_BOOKLET = 'release_documents_enable_benefit_booklet',
  DOCUMENT_CENTER_SBC = 'release_documents_enable_sbc',
  DOCUMENT_CENTER_EOP = 'release_documents_enable_eop',
  DOCUMENT_CENTER_RECENT_ERR_HANDLING = 'release_documents_enable_error_handle',
  DOCUMENT_CENTER_CASE_MANAGEMENT = 'release_documents_enable_call_recap',
  ENABLE_CARE_LIBRARY = 'release_mc_care_library',
}

export type FirebaseRemoteConfigValue = boolean | string | number;

export const MASONRY = {
  API_VERSION: 2,
  APP_ID_HOME_PAGE: 'hm_homepage',
  APP_ID_SUPPORT_PAGE: 'hm_support',
  APP_ID_CARE_DISCOVERY_HOME: 'hm_care_homepage',
  APP_ID_PWP_MAIN_PAGE: 'highmark_pwp',
  MODULE_KEY_CUSTOM_WIDGETS: 'highmark',
  MODULE_KEY_CLAIMS: 'claims',
  MODULE_KEY_HEALTH_JOURNEY: 'health_journey',
  CUSTOM_WIDGET_TYPE_EBILL: 'hm_ebill',
  CUSTOM_WIDGET_TYPE_HEALTH_JOURNEY: 'health_journey',
  CUSTOM_WIDGET_TYPE_PENDING_HSA: 'hm_pending_hsa_banner',
  CUSTOM_WIDGET_TYPE_PRE_EFFECTUATED_BANNER: 'hm_pre_effectuated_banner',
  CUSTOM_WIDGET_TYPE_PRE_EFFECTIVE_BANNER: 'hm_pre_effective_banner',
  CUSTOM_WIDGET_TYPE_PWP_HEADER: 'pwp_header',
  CUSTOM_WIDGET_TYPE_SECTION_HEADER_LINK: 'hm_section_header_with_link',
  CUSTOM_WIDGET_TYPE_CLAIMS: 'recent_claims_cards',
  CUSTOM_WIDGET_REWARD_PROGRAM_CAROUSEL: 'reward_programs_carousel',
  CUSTOM_WIDGET_REGION_BANNER: 'hm_region_banner',
  CUSTOM_WIDGET_TYPE_ACTIVITY_STACK: 'activity_stack',
  CUSTOM_WIDGET_TYPE_PWP_DISCLAIMER: 'pwp_disclaimer',
  NAMESPACE_HIGHMARK: 'highmark',
  NODE_TYPE_OVERVIEW_TEXT: 'overviewText',
  NODE_TYPE_BUTTON_SECONDARY: 'buttonSecondary',
  DEFAULT_NAMESPACE: 'default',
};

export const MASONRY_V2 = {
  NAMESPACE_DEFAULT: 'default',
  NAMESPACE_CUSTOM_WIDGET: 'highmark',
  NAMESPACE_CUSTOM_ACTION: 'highmark_action',
  NAMESPACE_EASY_PLAN: 'hmEasyPlanCta',
  NAMESPACE_REWARD_PROGRAMS_CAROUSEL: 'reward_programs',
  NAMESPACE_BENEFITS: 'benefits',
  CUSTOM_WIDGET_TYPE_REGION_BANNER: 'regionBanner',
  CUSTOM_WIDGET_TYPE_EASY_PLAN: 'ctaCard',
  CUSTOM_WIDGET_TYPE_REWARD_PROGRAM_CAROUSEL: 'rewardProgramsCarousel',
  CUSTOM_WIDGET_TYPE_SECTION_HEADER_LINK: 'sectionHeader',
  CUSTOM_WIDGET_TYPE_PENDING_HSA: 'hm_pending_hsa_banner',
  CUSTOM_WIDGET_TYPE_GENERIC_STATUS_BANNER: 'hm_pre_effective_banner',
  CUSTOM_WIDGET_TYPE_HEALTH_GOALS: 'healthGoals',
  CUSTOM_WIDGET_TYPE_REWARD_PROGRAMS_CARD: 'rewardProgramsCard',
  CUSTOM_WIDGET_TYPE_REWARD_PROGRAMS_CAROUSEL: 'carousel',
  CUSTOM_WIDGET_TYPE_STATUS_BANNER_WITH_MODAL: 'switchRegionStatusBanner',
  CUSTOM_WIDGET_TYPE_SIDEBAR_BUTTON: 'sidebarButton',
  ROOT_ENHANCED_BENEFITS: 'benefits$enhancedBenefits$root',
  WIDGET_TYPE_CTA_CARD: 'ctaCard',
};

export const LEGAL_DETAILS_QUERY_PARAM_KEY = 'id';

export const STORED_DEEPLINK_SESSION_STORAGE_KEY = 'stored_deeplink_url';

export const THIRD_PARTY_LEAVE_SITE_EXTERNAL_LINKS = {
  YMCA: 'https://www.ymca.org/what-we-do/healthy-living/fitness/diabetes-prevention#locations',
  LIVONGO: 'https://welcome.livongo.com/DPPHM?ccid=DPPWM#/',
};

export const PRODUCT_AREAS = {
  BENEFITS: 'benefits',
};

export const SUB_PRODUCT_AREAS = {
  ADDITIONAL_BENEFITS: 'additional benefits',
};

export const ANALYTICS_SCREEN_NAMES = {
  PWP: 'personalized wellness plan',
  JOURNEY: 'health journey',
  HOME: 'home screen',
  BENEFITS_LANDING: 'benefits landing screen',
  CARE_LANDING: 'get care landing screen',
  JOURNEY_LANDING: 'health journey landing screen',
  SUPPORT_LANDING: 'support landing screen',
  HEALTH_JOURNEY_ACTIVITIES: 'health journey activities',
};

export const DESKTOP_SINGLE_COLUMN_CONTENT_WIDTH = '920px';
export const MAX_DISPLAY_MESSAGES_COUNT = 9; // To indicate there are more messages than the specified limit.

export const HEALTH_JOURNEY_ASSESSMENT_LINK =
  '/campaign/14rD1Fw8nYXhJDhEwXRpMT/activity/5CzS660s7kaPkUiDYu3tyo/toolbox';

export enum CASE_MANAGEMENT_COHORT_TYPE {
  LONGITUDINAL = 'Longitudinal',
  EPISODIC = 'Episodic',
}

export enum CARE_MANAGEMENT_COHORT_MODAL_STATE {
  NOT_DISPLAYED = 'not-displayed',
  DISPLAYED = 'displayed',
}
export const CARE_MANAGEMENT_COHORT_MODAL_STATE_KEY =
  'care-management-cohort-modal-state-key';
